import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import ModelDetails from '../designer/ModelDetails';
import TemplateDetails from '../designer/TemplateDetails';
import AssetFilter from '../layout/AssetFilter';
import AssetList from '../layout/AssetList';
import Layout from '../layout/Layout';
import { ConfigContext } from '../../contexts/ConfigContext';
import { AssetSearchResultDTO, SearchAssetService } from '../../openapi/requests';
import { Filter } from '../../types/types';
import ModelpackDetails from '../designer/ModelpackDetails';
import MaterialDetails from '../designer/MaterialDetails';
import ModifierDetails from '../designer/ModifierDetails';
import { ReactComponent as InfoIcon } from '../../assets/icons/info.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search.svg';
import { ReactComponent as Search2Icon } from '../../assets/icons/search2.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg';
import { ReactComponent as CartIcon } from '../../assets/icons/cart.svg';
import './AssetSearch.scss';
import BottomDrawer from '../layout/BottomDrawer';
import { BASE_PATH } from '../..';
import { AnimatePresence, motion } from 'framer-motion';
import { AssetCartContext } from '../../contexts/AssetCartContext';
import AssetCart from './AssetCart';
import WorkflowAssetDetails from '../designer/WorkflowAssetDetails';
import { DialogContext } from '../../contexts/DialogContext';
import { ProjectDetail } from '../project/ProjectDetail';
import Overlay from '../layout/Overlay';
import OrderlineDetails from '../designer/OrderlineDetails';
import { ReactComponent as Spin360 } from "../../assets/icons/spin360.svg";
import { ReactComponent as Insitu } from "../../assets/icons/in-situ.svg";
import { ReactComponent as Cutout } from "../../assets/icons/cutout.svg";
import { ReactComponent as Animation } from "../../assets/icons/animation.svg";
import { OrderlineFileType } from '../../models/enums';
import { useInfiniteQuery } from '@tanstack/react-query';
import { AuthContext } from '../../contexts/AuthContext';

interface Props {
    selection: AssetSearchResultDTO[];
    onAssetSelected: (asset: AssetSearchResultDTO) => void;
    onSearchProjectsAll: () => void;
    onSearchProjectsAny: () => void;
}

const assetTypeNames: {[key:string]: string} = { 
   // 'model': 'Model', 
   // 'material': 'Material',
    'kfproduct': 'KF products',
    'prop': 'Props',
    'uploaded': 'Delivered assets',
    'final': 'Final assets',
    'project': 'Project',
    'scene': 'Scene', 
  //  'modifier': 'Modifier', 
    'workflowassets': 'Workflow',    
    //'orderline': 'Files' 
};

const AssetSearch: React.FC<Props> = ({ onAssetSelected, selection, onSearchProjectsAll, onSearchProjectsAny }) => {
    const { addToCart } = useContext(AssetCartContext);
    const { currentClient } = useContext(ConfigContext);
    const { hasRole } = useContext(AuthContext);
    const { error } = useContext(DialogContext);
    const [filters, setFilters] = useState<Filter[]>([]);
    const [query, setQuery] = useState('');
    const [value] = useDebounce(query, 250);
    const [sortBy, setSortBy] = useState("score");
    const [sortDirection, setSortDirection] = useState('desc');

    const [selectedAsset, setSelectedAsset] = useState<AssetSearchResultDTO | undefined>(undefined);
    const [showTemplateDetails, setShowTemplateDetails] = useState(false);
    const [showModelDetails, setShowModelDetails] = useState(false);
    const [showModelpackDetails, setShowModelpackDetails] = useState(false);
    const [showMaterialDetails, setShowMaterialDetails] = useState(false);
    const [showModifierDetails, setShowModifierDetails] = useState(false);
    const [showWorkflowDetails, setShowWorkflowDetails] = useState(false);
    const [showProjectDetails, setShowProjectDetails] = useState(false);
    const [showOrderlineDetails, setShowOrderlineDetails] = useState(false);

    const filter: Record<string, string[]> = useMemo(() => {
        var result: Record<string, string[]> = {
            "clientId": [currentClient.id.toString()]
        }

        if(!hasRole("Cadesign")){
            result["isEnabled"] = ["true"]
        }

        for (let i = 0; i < filters.length; i++) {
            const element = filters[i];
            result[element.property] = element.values.map(e => e.value.toString());
        }

        return result;
    }, [currentClient.id, filters, hasRole]);

    const {
        data: searchResult,
        fetchNextPage,
        hasNextPage,
        isLoading,
        isFetching,
    } = useInfiniteQuery({
        queryKey: [value, filter, sortBy, sortDirection],
        initialPageParam: "0",
        refetchInterval: false,
        refetchOnWindowFocus: false,
        queryFn: async ({ pageParam = "0" }) => {
            const from = parseInt(pageParam);
            return await SearchAssetService.getSearchAsset(value, filter, from === 0 ? ['metadata.brand', 'metadata.product_range', 'metadata.room', 'metadata.product_type_primary', 'metadata.category', 'assetType'] : [], from, 100, sortBy, sortDirection)
        },
        getNextPageParam: (lastPage, allPages) => {
            if (lastPage.next) {
                return lastPage.next.toString()
            }
            return null;
        }
    });

    const handleClick = useCallback((item: AssetSearchResultDTO) => {
        setSelectedAsset(item);

        if (item.assetType === "scene") {
            setShowTemplateDetails(true);
        }
        if (item.assetType === "model") {
            setShowModelDetails(true);
        }
        if (item.assetType === "modelpack") {
            setShowModelpackDetails(true);
        }
        if (item.assetType === "material") {
            setShowMaterialDetails(true);
        }
        if (item.assetType === "modifier") {
            setShowModifierDetails(true);
        }
        if (item.assetType === "workflowassets") {
            setShowWorkflowDetails(true);
        }
        if (item.assetType === "project") {
            setShowProjectDetails(true);
        }
        if (item.assetType === "orderline") {
            setShowOrderlineDetails(true);
        }
    }, []);

    const handleAssetSelect = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>, asset: AssetSearchResultDTO) => {
        e.preventDefault();
        e.stopPropagation();
        onAssetSelected(asset);
    }, [onAssetSelected]);

    const handleAssetAdd = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>, asset: AssetSearchResultDTO) => {
        e.preventDefault();
        e.stopPropagation();
        addToCart(asset).catch(reason => {
            error(undefined, reason)
        })
    }, [addToCart, error]);

    const renderOverlay = useCallback((asset: AssetSearchResultDTO) => {

        let icon: React.ReactElement | null = null;
        let label = asset.assetType;

        switch (asset.assetType) {
            case 'orderline':
                label = 'file';
                break;
            case 'workflowassets':
                label = 'workflow';
                break;
        }

        if (asset.assetType === "orderline") {
            if (asset.fileType === OrderlineFileType.Spin) {
                icon = <Spin360 />;
            }
            if (asset.fileType === OrderlineFileType.Cutout) {
                icon = <Cutout />;
            }
            if (asset.fileType === OrderlineFileType.Still) {
                icon = <Insitu />;
            }
            if (asset.fileType === OrderlineFileType.Animation) {
                icon = <Animation />;
            }
            if (asset.fileType === OrderlineFileType.Clip) {
                icon = <Animation />;
            }
        }

        return (
            <>
                <div className='buttons'>
                    <button><InfoIcon /></button>
                    {(asset.assetType === "model" || asset.assetType === "scene" || asset.assetType === "modelpack" || asset.assetType === "material" || asset.assetType === "modifier") && <button onClick={e => handleAssetSelect(e, asset)}><Search2Icon /></button>}
                    <button onClick={e => handleAssetAdd(e, asset)}><CartIcon /></button>
                </div>
                <span className='assettype tag'>
                    {label}
                    {icon}
                </span>
            </>
        )
    }, [handleAssetAdd, handleAssetSelect]);

    const filterValues = useMemo(() => {
        return searchResult?.pages[0].filters ?? [];
    }, [searchResult?.pages]);

    const brandValues = useMemo(() => {
        return filterValues.find(e => e.name === 'metadata.brand')?.values.map(e => ({ value: e.value, label: <>{e.value} <span>({e.count})</span></> }));
    }, [filterValues]);

    const rangeValues = useMemo(() => {
        return filterValues.find(e => e.name === 'metadata.product_range')?.values.map(e => ({ value: e.value, label: <>{e.value} <span>({e.count})</span></> }));
    }, [filterValues]);

    const roomValues = useMemo(() => {
        return filterValues.find(e => e.name === 'metadata.room')?.values.map(e => ({ value: e.value, label: <>{e.value} <span>({e.count})</span></> }));
    }, [filterValues]);

    const productTypeValues = useMemo(() => {
        return filterValues.find(e => e.name === 'metadata.product_type_primary')?.values.map(e => ({ value: e.value, label: <>{e.value} <span>({e.count})</span></> }));
    }, [filterValues]);

    const categoryValues = useMemo(() => {
        return filterValues.find(e => e.name === 'metadata.category')?.values.map(e => ({ value: e.value, label: <>{e.value} <span>({e.count})</span></> }));
    }, [filterValues]);

    const assetTypeValues = useMemo(() => {
        return filterValues.find(e => e.name === 'assetType')?.values.map(e => ({ value: e.value, label: <>{assetTypeNames[e.value]} <span>({e.count})</span></> }));
    }, [filterValues]);

    return (<Layout className='asset-search'>
        <header>
            <div>
                <div className='search-field'>
                    <SearchIcon />
                    <input type='search' value={query} onChange={e => setQuery(e.currentTarget.value)} placeholder='Type to search...' />
                </div>

                <AssetFilter disabled={filterValues.length === 0} filters={filters} onChange={e => setFilters(e)} items={[]} properties={[
                    { name: 'Brand', property: 'metadata.brand', type: 'select', itemsOverride: brandValues },
                    { name: 'Range', property: 'metadata.product_range', type: 'select', itemsOverride: rangeValues },
                    { name: 'Room', property: 'metadata.room', type: 'select', itemsOverride: roomValues },
                    { name: 'Product Type', property: 'metadata.product_type_primary', type: 'select', itemsOverride: productTypeValues },
                    { name: 'Category', property: 'metadata.category', type: 'select', itemsOverride: categoryValues },
                    { name: 'Asset Type', property: 'assetType', type: 'select', itemsOverride: assetTypeValues },
                ]} />

                
                <div className='order'>
                    <h3>Order by: </h3>
                    <button className={(sortBy === "score") ? "selected" : ""} onClick={() => { setSortBy("score"); setSortDirection('desc')}}>Relevance</button>
                    <button className={(sortBy === "title") ? "selected" : ""} onClick={() => { setSortBy("title"); setSortDirection('asc')}}>Name</button>
                    <button className={(sortBy === "timestampCreate") ? "selected" : ""} onClick={() => { setSortBy("timestampCreate"); setSortDirection('desc')}}>Newest</button>
                </div>
            </div>
        </header>
        <div className='search-page-container'>
            <AssetList loading={isLoading} assets={searchResult?.pages.flatMap(e => e.results) ?? []} displayMode='medium' onItemClick={handleClick} renderOverlay={renderOverlay} />
            {searchResult && <div className='search-page-loadmore'>
                <p>Showing {searchResult.pages.flatMap(e => e.results).length} results of {searchResult?.pages[0].totalResults ?? 0}</p>
                {hasNextPage && <button disabled={isFetching} className='secondary' onClick={() => fetchNextPage()}>Load more...</button>}
            </div>}
        </div>

        <AssetCart />

        <BottomDrawer open={selection.length > 0} size={220} modal={false} className='selection'>
            <div className='assets'>
                <AnimatePresence>
                    {selection.map(asset => <motion.div
                        key={asset.name}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className='asset'>
                        <img src={BASE_PATH + asset.thumbnail} alt={asset.title} />
                        <p>{asset.title}</p>
                        <div className='buttons'>
                            <button onClick={e => handleAssetSelect(e, asset)}><DeleteIcon /></button>
                        </div>
                    </motion.div>)}
                </AnimatePresence>
            </div>
            <div className='submit'>
                <h1>Find projects</h1>
                <button onClick={onSearchProjectsAll}>Using all selected assets</button>
                <button onClick={onSearchProjectsAny}>Using any of the selected assets</button>
            </div>
        </BottomDrawer>

        <TemplateDetails key={"template"} open={showTemplateDetails} onClose={() => setShowTemplateDetails(false)} templateId={selectedAsset?.id} />
        <ModelDetails key={"model"} open={showModelDetails} onClose={() => setShowModelDetails(false)} modelId={selectedAsset?.id} />
        <ModelpackDetails key={"modelpack"} open={showModelpackDetails} onClose={() => setShowModelpackDetails(false)} modelpackId={selectedAsset?.id} />
        <MaterialDetails key={"material"} open={showMaterialDetails} onClose={() => setShowMaterialDetails(false)} materialId={selectedAsset?.id} />
        <ModifierDetails key={"modifier"} open={showModifierDetails} onClose={() => setShowModifierDetails(false)} modifierId={selectedAsset?.id} />
        <WorkflowAssetDetails key={"workflow"} open={showWorkflowDetails} onClose={() => setShowWorkflowDetails(false)} asset={selectedAsset} />
        <OrderlineDetails key={"orderline"} open={showOrderlineDetails} onClose={() => setShowOrderlineDetails(false)} orderId={selectedAsset?.orderId} orderlineId={selectedAsset?.id} />
        <Overlay key={"project"} open={showProjectDetails} onClose={() => setShowProjectDetails(false)} className='asset-details'>
            <ProjectDetail projectId={showProjectDetails ? selectedAsset?.id : undefined} />
        </Overlay>
    </Layout>)
};

export default AssetSearch;